import { clsx } from 'clsx'
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { t } from '@/lib/i18n'
import { Icon } from './glyphs'
import { motionOn } from './motion'
import { EASE_IN, springEasing, springTo, SPRING_MS } from './springs'
import { PHONE_QUERY, useMediaQuery } from './use-media-query'

/**
 * Floating layers (APP-DESIGN-DIRECTION 2.6, 3.5). Two primitives:
 *
 * `Popover`: a non-modal panel anchored to its trigger (menus, the account card, the streak card). Desktop: fixed, placed
 * under (or over) the trigger, flipped and clamped so it never leaves the viewport at 320 to 1920px. Phone: the same
 * content as a bottom sheet over a scrim, with a grab handle you can drag down. It is a disclosure, not a `role="menu"`:
 * the caller's trigger carries `aria-expanded`; Escape and an outside press close it and focus returns to the trigger.
 *
 * `Sheet`: a modal (native `<dialog>` + `showModal()`, so focus is trapped and Escape works for free). Phone: bottom sheet
 * with the drag handle. Desktop: a centred dialog (forms) or a side panel from the inline end (`side`).
 *
 * Motion: menus open from the trigger edge (opacity in 120 ms, scale .96 and 4px on the snappy spring) and close in 120 ms
 * on `--r5-e-in`; sheets rise on the settle spring and leave in 220 ms; the scrim fades. Instant without the motion gate.
 */

type Placement = 'bottom-end' | 'bottom-start' | 'top-start' | 'top-end' | 'end-end'

interface Position { top: number; left: number; origin: string; maxHeight: number }

function place(anchor: DOMRect, panel: { width: number; height: number }, placement: Placement, rtl: boolean, gap = 8): Position {
  const vw = window.innerWidth
  const vh = window.innerHeight
  const margin = 8
  let vertical: 'bottom' | 'top' | 'side' = placement.startsWith('top') ? 'top' : placement.startsWith('end') ? 'side' : 'bottom'
  const alignEnd = placement.endsWith('end')
  let top: number
  let left: number
  if (vertical === 'side') {
    left = rtl ? anchor.left - gap - panel.width : anchor.right + gap
    top = anchor.bottom - panel.height
  } else {
    const below = vh - anchor.bottom - gap - margin
    const above = anchor.top - gap - margin
    if (vertical === 'bottom' && panel.height > below && above > below) vertical = 'top'
    else if (vertical === 'top' && panel.height > above && below > above) vertical = 'bottom'
    top = vertical === 'bottom' ? anchor.bottom + gap : anchor.top - gap - panel.height
    const endEdge = rtl ? !alignEnd : alignEnd
    left = endEdge ? anchor.right - panel.width : anchor.left
  }
  left = Math.min(Math.max(margin, left), vw - margin - panel.width)
  top = Math.min(Math.max(margin, top), vh - margin - Math.min(panel.height, vh - 2 * margin))
  const maxHeight = vh - 2 * margin
  const originX = anchor.left + anchor.width / 2 - left
  const originY = vertical === 'top' ? panel.height : vertical === 'side' ? panel.height : 0
  return { top, left, origin: `${Math.round(originX)}px ${Math.round(originY)}px`, maxHeight }
}

/** Keeps a mounted copy while the exit plays. */
function usePresence(open: boolean) {
  const [present, setPresent] = useState(open)
  useLayoutEffect(() => { if (open) setPresent(true) }, [open])
  return [present || open, setPresent] as const
}

/** Drag-to-dismiss for a phone sheet: follows the finger, closes past 30% or on a fling, springs back otherwise. */
function useSheetDrag(sheetRef: RefObject<HTMLElement | null>, onClose: () => void) {
  const state = useRef<{ y: number; t: number; dy: number; v: number } | null>(null)
  const onPointerDown = useCallback((event: React.PointerEvent) => {
    if (event.button !== 0) return
    state.current = { y: event.clientY, t: event.timeStamp, dy: 0, v: 0 }
    ;(event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId)
  }, [])
  const onPointerMove = useCallback((event: React.PointerEvent) => {
    const drag = state.current
    const sheet = sheetRef.current
    if (!drag || !sheet) return
    const raw = event.clientY - drag.y
    const dy = raw < 0 ? raw * 0.2 : raw
    const dt = Math.max(1, event.timeStamp - drag.t)
    drag.v = (dy - drag.dy) / dt
    drag.dy = dy
    drag.t = event.timeStamp
    sheet.style.transform = `translateY(${dy}px)`
  }, [sheetRef])
  const onPointerUp = useCallback(() => {
    const drag = state.current
    const sheet = sheetRef.current
    state.current = null
    if (!drag || !sheet) return
    const height = sheet.offsetHeight || 1
    if (drag.dy > height * 0.3 || drag.v > 0.8) {
      onClose()
      return
    }
    const from = sheet.style.transform
    sheet.style.transform = ''
    if (from) springTo(sheet, [{ transform: from }, { transform: 'none' }], 'settle', { fill: 'none' })
  }, [sheetRef, onClose])
  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp }
}

export interface PopoverProps {
  open: boolean
  onClose: () => void
  /** The trigger: placement reads its box, Escape and outside presses give focus back to it. */
  anchorRef: RefObject<HTMLElement | null>
  placement?: Placement
  /** The region's accessible name (a labelled `section`). */
  label: string
  id?: string
  /** Width on desktop. Default 320. */
  width?: number
  /** On a phone the content becomes a bottom sheet (default true). */
  sheetOnPhone?: boolean
  className?: string
  children: ReactNode
  /** Elements whose presses do not count as "outside" (a second trigger). */
  insideRefs?: Array<RefObject<HTMLElement | null>>
}

export function Popover({ open, onClose, anchorRef, placement = 'bottom-end', label, id, width = 320, sheetOnPhone = true, className, children, insideRefs = [] }: PopoverProps) {
  const phone = useMediaQuery(PHONE_QUERY)
  const asSheet = phone && sheetOnPhone
  const panelRef = useRef<HTMLElement>(null)
  const scrimRef = useRef<HTMLDivElement>(null)
  const [present, setPresent] = usePresence(open)
  const [position, setPosition] = useState<Position | null>(null)
  const rtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl'
  const drag = useSheetDrag(panelRef, onClose)

  // Outside press and Escape.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (panelRef.current?.contains(target) || anchorRef.current?.contains(target)) return
      if (insideRefs.some((ref) => ref.current?.contains(target))) return
      onClose()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      onClose()
      anchorRef.current?.focus()
    }
    window.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onClose, anchorRef, insideRefs])

  // Place it (desktop), and keep it placed on resize and scroll.
  useLayoutEffect(() => {
    if (!present || asSheet) return
    const update = () => {
      const anchor = anchorRef.current?.getBoundingClientRect()
      const panel = panelRef.current
      if (!anchor || !panel) return
      const w = Math.min(width, window.innerWidth - 16)
      setPosition(place(anchor, { width: w, height: panel.offsetHeight }, placement, rtl))
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [present, asSheet, anchorRef, placement, rtl, width])

  // Enter and exit.
  useLayoutEffect(() => {
    const panel = panelRef.current
    if (!panel) return
    if (open) {
      if (!motionOn() || typeof panel.animate !== 'function') return
      if (asSheet) {
        scrimRef.current?.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: 'linear' })
        panel.animate([{ transform: 'translateY(100%)' }, { transform: 'none' }], { duration: SPRING_MS.settle, easing: springEasing('settle') })
        panel.querySelectorAll(':scope > .r5-pop__body > *').forEach((child, index) => {
          child.animate([{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }], { duration: SPRING_MS.settle, delay: 80 + index * 40, easing: springEasing('settle'), fill: 'backwards' })
        })
      } else {
        panel.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 120, easing: 'linear' })
        panel.animate([{ transform: 'scale(.96) translateY(-4px)' }, { transform: 'none' }], { duration: SPRING_MS.snappy, easing: springEasing('snappy') })
      }
      return
    }
    if (!present) return
    if (!motionOn() || typeof panel.animate !== 'function') { setPresent(false); return }
    const exit = asSheet
      ? panel.animate([{ transform: getComputedStyle(panel).transform === 'none' ? 'none' : getComputedStyle(panel).transform }, { transform: 'translateY(100%)' }], { duration: 220, easing: EASE_IN, fill: 'forwards' })
      : panel.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'scale(.98)' }], { duration: 120, easing: EASE_IN, fill: 'forwards' })
    scrimRef.current?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 180, easing: 'linear', fill: 'forwards' })
    const done = () => setPresent(false)
    exit.onfinish = done
    exit.oncancel = done
    return () => exit.cancel()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, asSheet])

  // The panel takes focus on open. It is portalled away from its trigger (below), so Tab from the trigger no longer
  // reaches it; starting inside keeps it keyboard reachable, and Escape hands focus back to the trigger.
  useEffect(() => {
    if (open) panelRef.current?.focus({ preventScroll: true })
  }, [open])

  if (!present) return null
  const style = asSheet ? undefined : position
    ? { top: position.top, left: position.left, width: Math.min(width, window.innerWidth - 16), maxHeight: position.maxHeight, transformOrigin: position.origin }
    : { top: -9999, left: -9999, width }
  // Rendered at the trigger's `.r5` root (where the tokens live), not inside the trigger's container. A `position: fixed`
  // panel inside the admin sidebar was placed against the sidebar and clipped by it in Safari, which treats the
  // sidebar's `view-transition-name` as a containing block: the account menu opened invisibly and Sign out was unreachable.
  const host = anchorRef.current?.closest('.r5')
  const layer = (
    <>
      {asSheet && <div ref={scrimRef} className="r5-scrim" aria-hidden="true" />}
      <section
        ref={panelRef}
        id={id}
        aria-label={label}
        tabIndex={-1}
        className={clsx('r5-pop', asSheet ? 'r5-pop--sheet' : 'r5-pop--menu', !open && 'r5-pop--leaving', className)}
        style={style}
      >
        {asSheet && <div className="r5-sheet__grab" {...drag}><span /></div>}
        <div className="r5-pop__body">{children}</div>
      </section>
    </>
  )
  return host ? createPortal(layer, host) : layer
}

export interface SheetProps {
  open: boolean
  onClose: () => void
  title: string
  /** Optional line under the title. */
  description?: string
  /** Sticky footer: the action, and the "still missing" line when the form is not ready. */
  footer?: ReactNode
  /** Desktop shape: a centred dialog (default, forms) or a side panel from the inline end (record details). */
  variant?: 'dialog' | 'side'
  className?: string
  children: ReactNode
}

export function Sheet({ open, onClose, title, description, footer, variant = 'dialog', className, children }: SheetProps) {
  const phone = useMediaQuery(PHONE_QUERY)
  const ref = useRef<HTMLDialogElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const [present, setPresent] = usePresence(open)
  const drag = useSheetDrag(panelRef, onClose)

  useLayoutEffect(() => {
    const dialog = ref.current
    const panel = panelRef.current
    if (!dialog || !panel) return
    if (open) {
      if (!dialog.open) {
        try { dialog.showModal() } catch { dialog.setAttribute('open', '') }
      }
      dialog.querySelector<HTMLElement>('.r5-sheet__title')?.focus({ preventScroll: true })
      if (!motionOn() || typeof panel.animate !== 'function') return
      if (phone) panel.animate([{ transform: 'translateY(100%)' }, { transform: 'none' }], { duration: SPRING_MS.settle, easing: springEasing('settle') })
      else if (variant === 'side') panel.animate([{ opacity: 0, transform: 'translateX(calc(24px * var(--r5-dir)))' }, { opacity: 1, transform: 'none' }], { duration: SPRING_MS.settle, easing: springEasing('settle') })
      else panel.animate([{ opacity: 0, transform: 'scale(.96) translateY(8px)' }, { opacity: 1, transform: 'none' }], { duration: SPRING_MS.snappy, easing: springEasing('snappy') })
      return
    }
    if (!present) return
    const close = () => {
      if (dialog.open) {
        try { dialog.close() } catch { dialog.removeAttribute('open') }
      }
      setPresent(false)
    }
    if (!motionOn() || typeof panel.animate !== 'function') { close(); return }
    const exit = phone
      ? panel.animate([{ transform: 'none' }, { transform: 'translateY(100%)' }], { duration: 220, easing: EASE_IN, fill: 'forwards' })
      : panel.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'scale(.98)' }], { duration: 160, easing: EASE_IN, fill: 'forwards' })
    exit.onfinish = close
    exit.oncancel = close
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phone, variant])

  if (!present) return null
  return (
    <dialog
      ref={ref}
      className={clsx('r5-sheet', phone ? 'r5-sheet--phone' : `r5-sheet--${variant}`, className)}
      aria-labelledby={titleId}
      onCancel={(event) => { event.preventDefault(); onClose() }}
      onClick={(event) => { if (event.target === ref.current) onClose() }}
    >
      <div ref={panelRef} className="r5-sheet__panel">
        {phone && <div className="r5-sheet__grab" {...drag}><span /></div>}
        <header className="r5-sheet__head">
          <div>
            <h2 id={titleId} className="r5-sheet__title" tabIndex={-1}>{title}</h2>
            {description && <p className="r5-sheet__desc">{description}</p>}
          </div>
          <button type="button" className="r5-iconbtn r5-iconbtn--quiet r5-tap" aria-label={t('Close')} onClick={onClose}><Icon name="close" /></button>
        </header>
        <div className="r5-sheet__body">{children}</div>
        {footer && <footer className="r5-sheet__foot">{footer}</footer>}
      </div>
    </dialog>
  )
}
